import { memo, useMemo } from 'react';
import { CanvasEdgeLayer } from './CanvasEdgeLayer';
import { getHandler } from '../entities/registry';
import { useCanvasStores, useCanvasEntities, useCanvasSegments, useCanvasGroups } from '../stores/CanvasContext';
import { useHiddenLayerIds } from '../hooks/useHiddenLayerIds';
import { useGroupBoundsMap } from '../hooks/useGroupBoundsMap';
import { computeCollapseHidden, buildAugmentedNodeMap, rerouteCollapsedSegments } from '../collapseUtils';
import { useDependencyExplorerHighlight } from '../hooks/useDependencyExplorerHighlight';
import { NODE_WIDTH, NODE_HEIGHT } from '../entityTypes';
import type { EntityRectMap } from '../types';

function CanvasEdgePipelineInner() {
  const { measureMapRef } = useCanvasStores();
  const entities = useCanvasEntities();
  const segments = useCanvasSegments();
  const groups = useCanvasGroups();
  const hiddenLayerIds = useHiddenLayerIds();
  const groupBoundsMap = useGroupBoundsMap();

  const { hiddenNodeToGroupId, hiddenGroupIds } = useMemo(
    () => computeCollapseHidden(groups), [groups],
  );

  const entityLayerMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entities) {
      if (e.kind === 'task' || e.kind === 'textblock' || e.kind === 'textlabel' || e.kind === 'sprite' || e.kind === 'image' || e.kind === 'blocknode' || e.kind === 'shape' || e.kind === 'adr-trigger') {
        map.set(e.id, e.layerId);
      }
    }
    return map;
  }, [entities]);

  const nodeMap = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const e of entities) {
      if (e.kind === 'task') map.set(e.id, { x: e.x, y: e.y });
    }
    return map;
  }, [entities]);

  const layerFilteredSegments = useMemo(() => {
    if (hiddenLayerIds.size === 0) return segments;
    return segments.filter(s => {
      for (const ep of [s.from, s.to]) {
        const layerId = entityLayerMap.get(ep.entityId);
        if (layerId && hiddenLayerIds.has(layerId)) return false;
      }
      return true;
    });
  }, [segments, hiddenLayerIds, entityLayerMap]);

  const augmentedNodeMap = useMemo(
    () => buildAugmentedNodeMap(nodeMap, groups, groupBoundsMap, hiddenGroupIds),
    [nodeMap, groups, groupBoundsMap, hiddenGroupIds],
  );

  const entityRectMap = useMemo<EntityRectMap>(() => {
    const map: EntityRectMap = new Map();
    for (const [id, pos] of augmentedNodeMap) {
      map.set(id, { x: pos.x, y: pos.y, width: NODE_WIDTH, height: NODE_HEIGHT });
    }
    for (const e of entities) {
      if (e.kind === 'task' || e.kind === 'group' || e.kind === 'connector') continue;
      const handler = getHandler(e.kind);
      if (handler.autoMeasure) {
        const measured = measureMapRef.current.get(e.id);
        map.set(e.id, { x: e.x, y: e.y, width: measured?.width ?? 0, height: measured?.height ?? 0 });
      } else {
        map.set(e.id, { x: e.x, y: e.y, width: e.width, height: e.height });
      }
    }
    return map;
  }, [augmentedNodeMap, entities, measureMapRef]);

  const reroutedSegments = useMemo(
    () => rerouteCollapsedSegments(layerFilteredSegments, hiddenNodeToGroupId, groupBoundsMap, entityRectMap),
    [layerFilteredSegments, hiddenNodeToGroupId, groupBoundsMap, entityRectMap],
  );

  const depHighlight = useDependencyExplorerHighlight();

  return (
    <CanvasEdgeLayer
      segments={reroutedSegments}
      entityRectMap={entityRectMap}
      depExplorerActive={depHighlight.isActive}
      highlightedConnectorIds={depHighlight.highlightedConnectorIds}
    />
  );
}

export const CanvasEdgePipeline = memo(CanvasEdgePipelineInner);
